import { CloseOutlined, CommentOutlined, DownOutlined, SendOutlined } from "@ant-design/icons";
import { Button, FloatButton, Input } from "antd";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { GetMessageFilter } from "../API/GetData";
import { PostChat } from "../API/PostData";
import dayjs from "dayjs";
import utc from 'dayjs/plugin/utc';
import { AuthData } from "./Global";

dayjs.extend(utc);

const LiveChat = () => {
    const [message, setMessage] = useState("");
    const [isChatVisible, setChatVisible] = useState(false);
    const [chats, setChats] = useState(null);
    const userAuth = AuthData();
    const AccountId = userAuth?.AccountId;
    const CustomerId = userAuth?.CustomerId;
    const Role = userAuth?.Role;

    const chatContainerRef = useRef(null);

    useEffect(() => {
        if (chatContainerRef.current) {
            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        }
    }, [chats]);

    const toggleChat = () => {
        setChatVisible((prev) => !prev);
    };

    const fetchData = useCallback(async () => {
        try {
            const res = await GetMessageFilter(AccountId, CustomerId);
            setChats(res);
        } catch (error) {
            console.error("Error fetching chat data:", error);
        }
    }, [AccountId, CustomerId]);

    useEffect(() => {
        if (isChatVisible) {
            fetchData();
        }
    }, [fetchData, isChatVisible]);

    const handleSend = async () => {
        if (!message.trim()) return;
        try {
            const payload = {
                ChatId: chats?.length > 0 ? chats[chats.length - 1].ChatId : "",
                AccountId,
                CustomerId,
                ChatDate: dayjs().format('YYYY-MM-DD HH:mm:ss'),
                Message: message,
                Role
            };

            const res = await PostChat(payload);
            if (res.status === 201) {
                fetchData();
                setMessage("");
            }
        } catch (error) {
            console.error("Error sending message:", error);
        }
    };

    let prevDate = null;

    return (
        <div>
            <FloatButton
                icon={isChatVisible ? <CloseOutlined /> : <CommentOutlined />}
                onClick={toggleChat}
                className="mb-4"
                type="primary"
            />

            <div className={`fixed bottom-24 right-4 z-50 shadow-xl rounded-xl transition-all duration-300 origin-bottom-right ${isChatVisible ? "scale-100 opacity-100" : "scale-0 opacity-0"}`}>
                <div className="bg-black text-white text-[16px] h-12 rounded-t-xl flex items-center justify-between px-4">
                    <p className=" font-bold">Live Chat Support</p>
                    <button onClick={toggleChat} className="hover:scale-110 transition-all duration-200">
                        <DownOutlined />
                    </button>
                </div>
                <div ref={chatContainerRef} className="flex flex-col bg-gray-100 w-[360px] h-[420px] overflow-y-auto px-4 py-2">
                    {chats?.length > 0 ? chats.map((msg, index) => {
                        const currentDate = dayjs.utc(msg.MessageDate).format('DD MMMM YYYY');
                        const showDate = currentDate !== prevDate;
                        prevDate = currentDate;

                        return (
                            <div key={index}>
                                {showDate && <div className="w-full text-center font-semibold mt-4 mb-2">{currentDate}</div>}
                                <p className={`text-xs mb-0.5 ${msg.SenderType === "User" ? "text-right" : "text-left"}`}>
                                    {msg.SenderType === "User" ? msg.CustomerName : "Admin"}
                                </p>
                                <div className={`w-3/4 shadow-md p-2 rounded-lg mb-4 ${msg.SenderType === "User" ? "bg-primary-500 text-white ml-auto" : "bg-white"}`}>
                                    <div className="flex justify-between items-end">
                                        <p>{msg.Message}</p>
                                        <p className="text-xs text-right opacity-70">{dayjs.utc(msg.MessageDate).format('HH:mm')}</p>
                                    </div>
                                </div>
                            </div>
                        );
                    }) : (
                        <div className="h-full w-full flex flex-col items-center justify-center gap-2 text-gray-500">
                            <CommentOutlined style={{ fontSize: "64px" }} />
                            <p className="text-lg flex flex-col items-center">
                                <span>Welcome to Live Chat</span>
                                <span>Empty chat...</span>
                            </p>
                        </div>
                    )}
                </div>
                <div className="flex items-center bg-white h-16 rounded-b-xl gap-2 p-2 border-t">
                    <Input
                        size="large"
                        placeholder="Type a message..."
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        onPressEnter={handleSend}
                    />
                    <Button type="primary" shape="circle" icon={<SendOutlined />} onClick={handleSend} disabled={!message.trim()} />
                </div>
            </div>
        </div>
    );
};

export default LiveChat;
